<template>
  <div class="licenses-v3">
    <v-alert v-if="loadError" type="error">{{ loadError }}</v-alert>
    <v-alert v-if="message" type="success" dismissible @input="message = ''">
      {{ message }}
    </v-alert>

    <v-data-table
      :headers="headers"
      :items="licenses"
      :search="search"
      :loading="loading"
      sort-by="snumber"
      class="elevation-1"
      no-data-text="No v3 licenses found."
      @dblclick:row="onDoubleClick"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title><h2 class="br">Subscription Licenses</h2></v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn
            icon
            :disabled="loading || saving"
            aria-label="Refresh licenses"
            @click="initialize"
          >
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-toolbar>
        <v-toolbar flat>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
        </v-toolbar>
      </template>

      <template v-slot:item.created_at="{ item }">
        {{ formatDate(item.created_at) }}
      </template>

      <template v-slot:item.v3_enabled="{ item }">
        <v-chip
          small
          dark
          :color="item.v3_enabled ? 'green darken-2' : 'red darken-2'"
        >
          {{ item.v3_enabled ? "Yes" : "No" }}
        </v-chip>
      </template>

      <template v-slot:item.status="{ item }">
        <v-chip
          small
          dark
          :color="item.status ? 'green darken-2' : 'red darken-2'"
          title="Active means enabled with a current subscription. BR release eligibility is checked separately."
        >
          {{ item.status ? "Active" : "Inactive" }}
        </v-chip>
      </template>

      <template v-slot:item.subscription_text="{ item }">
        <div v-if="item.subscription" class="py-2">
          <div>{{ formatBandwidth(item.subscription.bandwidth) }}</div>
          <div>
            {{ formatDate(item.subscription.starts_at) }} —
            {{ formatDate(item.subscription.ends_at) }}
          </div>
          <div class="text-caption">
            {{ formatCutoff(item.subscription.br_release_cutoff) }}
          </div>
        </div>
        <span v-else>No current subscription</span>
      </template>

      <template v-slot:item.actions="{ item }">
        <v-btn
          icon
          small
          :aria-label="'Edit license ' + item.snumber"
          @click.stop="editItem(item)"
        >
          <v-icon small>mdi-pencil</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="dialog" :persistent="saving" max-width="550px">
      <v-card>
        <v-form ref="editForm" @submit.prevent="save">
          <v-card-title>
            <span class="headline">Subscription License</span>
          </v-card-title>
          <v-card-text>
            <v-alert v-if="saveError" type="error">{{ saveError }}</v-alert>
            <v-text-field
              :value="editedItem.snumber"
              label="Serial number"
              readonly
            ></v-text-field>
            <v-text-field
              :value="formatDate(editedItem.created_at)"
              label="Created"
              readonly
            ></v-text-field>
            <v-switch
              v-if="editedItem.has_legacy_license"
              v-model="editedItem.v3_enabled"
              label="Enabled"
              color="green darken-2"
              :disabled="saving"
              hint="Enabling this disables the linked legacy license."
              persistent-hint
              class="mb-4"
            ></v-switch>
            <v-text-field
              v-model="editedItem.info"
              label="Description"
              :disabled="saving"
            ></v-text-field>
            <v-text-field
              :value="editedBandwidth"
              label="Bandwidth"
              readonly
            ></v-text-field>
            <div v-if="editedItem.subscription" class="mb-4">
              <div>
                Subscription: {{ formatDate(editedItem.subscription.starts_at) }} —
                {{ formatDate(editedItem.subscription.ends_at) }}
              </div>
              <div>
                {{ formatCutoff(editedItem.subscription.br_release_cutoff) }}
              </div>
            </div>
            <v-text-field
              v-model="editedItem.passwd"
              label="New password"
              type="password"
              autocomplete="new-password"
              maxlength="32"
              :counter="32"
              :rules="passwordRules"
              :disabled="saving"
              hint="Leave blank to keep the current password."
              persistent-hint
            ></v-text-field>
            <v-file-input
              v-model="dmiLicenseFile"
              label="DMI.data"
              show-size
              :rules="dmiRules"
              :disabled="saving"
              hint="Optional hardware file, up to 8 KiB."
              persistent-hint
            ></v-file-input>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn
              color="blue darken-1"
              text
              :disabled="saving"
              @click="dialog = false"
            >
              Cancel
            </v-btn>
            <v-btn color="blue darken-1" text type="submit" :loading="saving">
              Save
            </v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api_client, { http_client } from "../services/api.js";

// Match the backend's existing 8 KiB hardware-file limit.
const MAX_DMI_BYTES = 8 * 1024;
const emptyLicense = () => ({
  id: null,
  info: "",
  snumber: "",
  created_at: null,
  v3_enabled: false,
  has_legacy_license: false,
  subscription: null,
  passwd: ""
});

export default {
  name: "licenses_v3",

  data: () => ({
    search: "",
    licenses: [],
    loading: false,
    saving: false,
    dialog: false,
    loadError: "",
    saveError: "",
    message: "",
    dmiLicenseFile: null,
    editedItem: emptyLicense(),
    headers: [
      { text: "Description", value: "info", align: "start", sortable: false },
      { text: "Serial number", value: "snumber" },
      { text: "Created", value: "created_at" },
      { text: "Enabled", value: "v3_enabled" },
      { text: "Status", value: "status", sortable: true },
      { text: "Subscription", value: "subscription_text", sortable: false },
      { text: "Actions", value: "actions", sortable: false }
    ],
    passwordRules: [
      value => !value || value.length <= 32 || "Use at most 32 characters."
    ],
    dmiRules: [
      file =>
        !file ||
        (file.size > 0 && file.size <= MAX_DMI_BYTES) ||
        "Choose a nonempty file no larger than 8 KiB."
    ]
  }),

  computed: {
    editedBandwidth() {
      return this.editedItem.subscription
        ? this.formatBandwidth(this.editedItem.subscription.bandwidth)
        : "No current subscription";
    }
  },

  watch: {
    dialog(open) {
      if (!open) {
        this.editedItem = emptyLicense();
        this.dmiLicenseFile = null;
        this.saveError = "";
      }
    }
  },

  created() {
    this.initialize();
  },

  methods: {
    async initialize() {
      this.loading = true;
      this.loadError = "";
      try {
        const response = await api_client.get_licenses_v3();
        this.licenses = response.data.map(license => ({
          ...license,
          // A text value lets the grid search the nested subscription fields.
          subscription_text: this.subscriptionText(license.subscription)
        }));
      } catch (error) {
        this.loadError = this.errorMessage(
          error,
          "Unable to load v3 licenses. Please try again."
        );
      } finally {
        this.loading = false;
      }
    },

    formatDate(value) {
      if (!value) return "";
      return new Date(value).toLocaleDateString("de-DE", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        timeZone: "UTC"
      });
    },

    formatBandwidth(value) {
      // Subscription bandwidth is stored in kbit/s.
      if (value >= 1000000) return value / 1000000 + " Gbit/s";
      if (value >= 1000) return value / 1000 + " Mbit/s";
      return value + " kbit/s";
    },

    formatCutoff(value) {
      return value ? "BR releases through " + this.formatDate(value) : "All BR releases";
    },

    subscriptionText(subscription) {
      if (!subscription) return "No current subscription";
      return [
        this.formatBandwidth(subscription.bandwidth),
        this.formatDate(subscription.starts_at) + " — " + this.formatDate(subscription.ends_at),
        this.formatCutoff(subscription.br_release_cutoff)
      ].join("; ");
    },

    editItem(item) {
      if (this.saving) return;
      this.editedItem = { ...item, passwd: "" };
      this.dmiLicenseFile = null;
      this.saveError = "";
      this.message = "";
      this.dialog = true;
      this.$nextTick(() => {
        if (this.$refs.editForm) this.$refs.editForm.resetValidation();
      });
    },

    onDoubleClick(event, row) {
      this.editItem(row.item);
    },

    errorMessage(error, fallback) {
      const detail =
        error.response && error.response.data && error.response.data.detail;
      if (typeof detail === "string") return detail;
      if (Array.isArray(detail)) return detail.map(item => item.msg).join("; ");
      return fallback;
    },

    update_license_v3(license_id, info, passwd, dmi_license_file, v3_enabled) {
      const form_data = new FormData();
      form_data.append("info", info);
      form_data.append("passwd", passwd);
      if (typeof v3_enabled === "boolean") {
        form_data.append("v3_enabled", v3_enabled ? "true" : "false");
      }
      if (dmi_license_file) {
        form_data.append("dmi_license_file", dmi_license_file);
      }
      return http_client.post(`/licenses_v3/${license_id}`, form_data, {
        headers: { "Content-Type": "multipart/form-data" }
      });
    },

    async save() {
      if (this.saving || !this.$refs.editForm.validate()) return;
      this.saving = true;
      this.saveError = "";
      const serial = this.editedItem.snumber;
      try {
        await this.update_license_v3(
          this.editedItem.id,
          this.editedItem.info,
          this.editedItem.passwd,
          this.dmiLicenseFile,
          this.editedItem.has_legacy_license
            ? this.editedItem.v3_enabled
            : undefined
        );
        // Reflect the confirmed edit even if refreshing the list later fails.
        const item = this.licenses.find(
          license => license.id === this.editedItem.id
        );
        if (item) {
          item.info = this.editedItem.info;
          item.v3_enabled = this.editedItem.v3_enabled;
          item.status = item.v3_enabled && Boolean(item.subscription);
        }
        this.dialog = false;
        this.message = "License #" + serial + " has been successfully updated.";
        await this.initialize();
      } catch (error) {
        this.saveError = this.errorMessage(
          error,
          "Unable to save the license. Please try again."
        );
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
